/**
 * テナントごとのUI設定
 *
 * 各テナントのホーム画面とフッターの表示設定を管理します。
 * 新しいテナントを追加する場合は、TENANT_UI_CONFIG にエントリを追加してください。
 */

/**
 * ホーム画面のアクションカード設定
 */
export type HomeActionCardsConfig = {
  /** 「支払う」ボタンの表示 */
  showPay: boolean;
  /** 「送る」ボタンの表示 */
  showSend: boolean;
  /** 「受け取る」ボタンの表示 */
  showReceive: boolean;
};

/**
 * フッター設定
 */
export type FooterConfig = {
  /** 「店舗情報」タブの表示 */
  showStoreInfo: boolean;
  /** 「設定」タブの表示 */
  showSettings: boolean;
};

/**
 * テナントUI設定
 */
export type TenantUIConfig = {
  homeActionCards: HomeActionCardsConfig;
  footer: FooterConfig;
};

/**
 * テナントごとのUI設定マップ
 *
 * 新しいテナントを追加する場合はここにエントリを追加してください。
 * 未登録のテナントはデフォルト設定（全て表示）が適用されます。
 */
const TENANT_UI_CONFIG: Record<string, TenantUIConfig> = {
  // miyagi: 全て表示。設定タブを有効化
  miyagi: {
    homeActionCards: {
      showPay: true,
      showSend: true,
      showReceive: true,
    },
    footer: {
      showStoreInfo: true,
      showSettings: true,
    },
  },

  // demo: 全て表示（miyagiと同様。設定タブも有効）
  demo: {
    homeActionCards: {
      showPay: true,
      showSend: true,
      showReceive: true,
    },
    footer: {
      showStoreInfo: true,
      showSettings: true,
    },
  },

  // marumori: 受け取るを非表示
  marumori: {
    homeActionCards: {
      showPay: true,
      showSend: true,
      showReceive: false,
    },
    footer: {
      showStoreInfo: true,
      showSettings: false,
    },
  },

  // shiga-otsu: カードは全て非表示、フッターから店舗情報を削除
  "shiga-otsu": {
    homeActionCards: {
      showPay: false,
      showSend: false,
      showReceive: false,
    },
    footer: {
      showStoreInfo: false,
      showSettings: false,
    },
  },

  // shiga-otsu-pay: 1ポイント1円で店舗利用できる支払い用テナント。決済・送金・店舗一覧を有効化。
  "shiga-otsu-pay": {
    homeActionCards: {
      showPay: true,
      showSend: true,
      showReceive: true,
    },
    footer: {
      showStoreInfo: true,
      showSettings: false,
    },
  },

  // kyoto-kyoto: 送る・受け取るを非表示
  "kyoto-kyoto": {
    homeActionCards: {
      showPay: true,
      showSend: false,
      showReceive: false,
    },
    footer: {
      showStoreInfo: true,
      showSettings: false,
    },
  },
};

/**
 * デフォルトのUI設定（全て表示）
 *
 * 未登録のテナントにはこの設定が適用されます。
 */
const DEFAULT_UI_CONFIG: TenantUIConfig = {
  homeActionCards: {
    showPay: true,
    showSend: true,
    showReceive: true,
  },
  footer: {
    showStoreInfo: true,
    showSettings: false,
  },
};

/**
 * テナントのUI設定を取得する
 *
 * @param tenantSlug - テナントのスラッグ（例: "miyagi", "marumori", "shiga-otsu"）
 * @returns テナントのUI設定
 */
export function getTenantUIConfig(tenantSlug: string): TenantUIConfig {
  return TENANT_UI_CONFIG[tenantSlug] ?? DEFAULT_UI_CONFIG;
}

/**
 * ホーム画面のアクションカード設定を取得する
 *
 * @param tenantSlug - テナントのスラッグ
 * @returns ホーム画面のアクションカード設定
 */
export function getHomeActionCardsConfig(
  tenantSlug: string,
): HomeActionCardsConfig {
  return getTenantUIConfig(tenantSlug).homeActionCards;
}

/**
 * フッター設定を取得する
 *
 * @param tenantSlug - テナントのスラッグ
 * @returns フッター設定
 */
export function getFooterConfig(tenantSlug: string): FooterConfig {
  return getTenantUIConfig(tenantSlug).footer;
}

/**
 * アクションカードが1つでも表示されるかを判定する
 *
 * @param tenantSlug - テナントのスラッグ
 * @returns アクションカードが表示される場合はtrue
 */
export function hasAnyActionCard(tenantSlug: string): boolean {
  const config = getHomeActionCardsConfig(tenantSlug);
  return config.showPay || config.showSend || config.showReceive;
}
